import { CURSOR_WORKSPACE_MEMBERS_V2 } from '@/graphql/cursor.graphql';
import {
  useCountMembersQuery,
  UserWorkspaceStatusEnum,
  WorkspaceMembersV2Query,
  WorkspaceMembersV2QueryVariables,
} from '@/types/generated/graphql';
import { useCursorPagination } from '@shared/hooks/use-cursor-pagination';
import { useAssignChannels } from './hooks/use-assign-channels';
import { useUpdateMember } from './hooks/use-edit-members';
import { useMemberRouting } from './hooks/use-member-routing';
import { STATUS_TYPES, StatusType, UseMembersFn } from './members.types';
import { useDeleteMember } from './hooks/use-delete-member';
import { useInvitation } from './hooks/use-invitation';

export const getStatus = (status: StatusType): UserWorkspaceStatusEnum | null => {
  switch (status) {
    case STATUS_TYPES.ALL:
      return null;
    case STATUS_TYPES.ACTIVE:
      return UserWorkspaceStatusEnum.accepted;
    case STATUS_TYPES.INVITES:
      return UserWorkspaceStatusEnum.pending;
    default:
      return null;
  }
};

export const useMembers: UseMembersFn = () => {
  const memberRouting = useMemberRouting();
  const { searchQuery, searchParams } = memberRouting;
  const { data: membersCountData } = useCountMembersQuery();

  const status = (searchParams.get('status') as StatusType) || STATUS_TYPES.ALL;

  const cursorWorkspaceMembersV2Data = useCursorPagination<WorkspaceMembersV2Query, WorkspaceMembersV2QueryVariables>({
    schema: CURSOR_WORKSPACE_MEMBERS_V2,
    variables: {
      query: searchQuery,
      limit: 20,
      status: getStatus(status),
      order: 'createdAt',
    },
    responseDataKey: 'workspaceMembersV2',
  });

  const updateMember = useUpdateMember();
  const assignChannel = useAssignChannels();
  const deleteMember = useDeleteMember();
  const mutateInvite = useInvitation();

  const membersCount = membersCountData?.countMembers?.data?.find((item) => item?.key === 'total')?.value || 0;

  const membersCountAccepted =
    membersCountData?.countMembers?.data?.find((item) => item?.key === 'accepted')?.value || 0;

  return {
    status,
    ...memberRouting,
    ...updateMember,
    ...mutateInvite,
    ...assignChannel,
    ...deleteMember,
    searchQuery,
    data: cursorWorkspaceMembersV2Data,
    membersCount,
    membersCountAccepted,
  };
};
